import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { GrowthAgentService, GrowthStrategyArtifact } from './growth-agent.service';
import { normalizeStrategyArtifactStatus, normalizeStrategyArtifactType, STRATEGY_ARTIFACT_TYPE_OPTIONS, strategyArtifactTypeI18nKey } from './growth-status.util';
import { isRichStrategyArtifact } from './growth-strategy-artifact-content.util';

interface StrategyArtifactFormModel {
  artifactType: string;
  title: string;
  objective: string;
  targetSegment: string;
  budgetRange: string;
  timeline: string;
  kpis: string;
  channels: string;
  campaigns: string;
  messaging: string;
  successMetrics: string;
  linkedBusinessPlanId: string | null;
  strengths: string;
  weaknesses: string;
  opportunities: string;
  threats: string;
}

@Component({
  standalone: true,
  selector: 'ain-growth-strategy-artifacts',
  imports: [
    FormsModule,
    AinPageComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    SelectModule,
    InputTextModule,
    TextareaModule,
    RouterLink
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.strategy.title')" [description]="t('growth.strategy.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.strategy.help.title'"
        [descriptionKey]="'growth.strategy.help.description'"
        [bulletKeys]="['growth.strategy.help.bullets.structured','growth.strategy.help.bullets.approval']" />

      <section class="card">
        <div class="toolbar">
          <a pButton [routerLink]="['/growth/agents/strategy-artifacts/new']" [label]="t('growth.strategy.actions.newArtifact')"></a>
          <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
        </div>

        <p-table [value]="items()" [rows]="10" [paginator]="true">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.strategy.columns.title') }}</th>
              <th>{{ t('growth.strategy.columns.type') }}</th>
              <th>{{ t('growth.strategy.columns.status') }}</th>
              <th>{{ t('growth.strategy.columns.linkedBusinessPlan') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.title }}</td>
              <td>{{ artifactTypeLabel(row.artifactType) }}</td>
              <td>{{ strategyArtifactStatusLabel(row.status) }}</td>
              <td>{{ linkedBusinessPlanTitle(row) }}</td>
              <td class="actions">
                @if (canEdit(row)) {
                  <a pButton type="button" size="small" severity="secondary" [routerLink]="['/growth/agents/strategy-artifacts', row.id]" [label]="t('common.edit')"></a>
                }
                @if (isStrategyArtifactDraft(row.status)) {
                  <button pButton type="button" size="small" [label]="t('growth.strategy.actions.submit')" (click)="submit(row.id)"></button>
                }
                @if (isStrategyArtifactInReview(row.status)) {
                  <button pButton type="button" size="small" [label]="t('growth.strategy.actions.approve')" (click)="approve(row.id)"></button>
                }
                @if (canGenerateMarketingPlan(row)) {
                  <button pButton type="button" size="small" severity="help" [label]="t('growth.strategy.actions.generateMarketingPlan')" (click)="generateMarketingPlan(row)"></button>
                }
                @if (canGenerateCalendar(row)) {
                  <button pButton type="button" size="small" severity="contrast" [label]="t('growth.strategy.actions.generateCalendar')" (click)="openActivationPlans(row.id)"></button>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
      </section>

      <p-dialog
        [header]="dialogMode() === 'edit' ? t('growth.strategy.dialog.editTitle') : t('growth.strategy.dialog.createTitle')"
        [visible]="dialogVisible()"
        (visibleChange)="dialogVisible.set($event)"
        [modal]="true"
        [style]="{ width: 'min(920px, 96vw)' }">
        <ain-info-hint
          [titleKey]="'growth.strategy.dialog.help.title'"
          [descriptionKey]="'growth.strategy.dialog.help.description'"
          [bulletKeys]="[
            'growth.strategy.dialog.help.bullets.swot',
            'growth.strategy.dialog.help.bullets.approval',
            'growth.strategy.dialog.help.bullets.activation'
          ]" />

        <div class="dialog-grid">
          <ain-form-field [label]="t('growth.strategy.fields.type')">
            <p-select
              [options]="typeOptions()"
              [(ngModel)]="model.artifactType"
              optionLabel="label"
              optionValue="value"
              [disabled]="dialogMode() === 'edit'"
              appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.strategy.fields.title')">
            <input pInputText class="dialog-input" [(ngModel)]="model.title" />
          </ain-form-field>
          @if (isBusinessPlanType()) {
            <ain-form-field [label]="t('growth.strategy.businessPlan.fields.objective')">
              <textarea pInputTextarea class="dialog-textarea" rows="3" [(ngModel)]="model.objective"></textarea>
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.businessPlan.fields.targetSegment')">
              <input pInputText class="dialog-input" [(ngModel)]="model.targetSegment" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.businessPlan.fields.budgetRange')">
              <input pInputText class="dialog-input" [(ngModel)]="model.budgetRange" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.businessPlan.fields.timeline')">
              <input pInputText class="dialog-input" [(ngModel)]="model.timeline" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.businessPlan.fields.kpis')">
              <textarea pInputTextarea class="dialog-textarea" rows="3" [(ngModel)]="model.kpis"></textarea>
            </ain-form-field>
          } @else if (isMarketingPlanType()) {
            <ain-form-field [label]="t('growth.strategy.marketingPlan.fields.businessPlan')">
              <p-select
                [options]="businessPlanOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="model.linkedBusinessPlanId"
                [showClear]="true"
                appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.marketingPlan.fields.channels')">
              <textarea pInputTextarea class="dialog-textarea" rows="3" [(ngModel)]="model.channels"></textarea>
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.marketingPlan.fields.campaigns')">
              <textarea pInputTextarea class="dialog-textarea" rows="3" [(ngModel)]="model.campaigns"></textarea>
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.marketingPlan.fields.messaging')">
              <textarea pInputTextarea class="dialog-textarea" rows="3" [(ngModel)]="model.messaging"></textarea>
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.marketingPlan.fields.successMetrics')">
              <textarea pInputTextarea class="dialog-textarea" rows="3" [(ngModel)]="model.successMetrics"></textarea>
            </ain-form-field>
          } @else {
            <ain-form-field [label]="t('growth.strategy.fields.strengths')">
              <textarea pInputTextarea class="dialog-textarea" rows="5" [(ngModel)]="model.strengths"></textarea>
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.fields.weaknesses')">
              <textarea pInputTextarea class="dialog-textarea" rows="5" [(ngModel)]="model.weaknesses"></textarea>
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.fields.opportunities')">
              <textarea pInputTextarea class="dialog-textarea" rows="5" [(ngModel)]="model.opportunities"></textarea>
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.fields.threats')">
              <textarea pInputTextarea class="dialog-textarea" rows="5" [(ngModel)]="model.threats"></textarea>
            </ain-form-field>
          }
        </div>

        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="dialogVisible.set(false)"></button>
          <button pButton type="button" [label]="dialogMode() === 'edit' ? t('common.save') : t('common.create')" [disabled]="!canSave()" (click)="save()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; }
    .dialog-grid { display: grid; gap: 14px; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); margin-top: 12px; }
    .dialog-input { width: 100%; font-size: 1rem; line-height: 1.5; padding: 0.55rem 0.75rem; }
    .dialog-textarea {
      width: 100%;
      min-height: 7.5rem;
      font-size: 0.95rem;
      line-height: 1.55;
      padding: 0.65rem 0.75rem;
      resize: vertical;
    }
  `]
})
export class GrowthStrategyArtifactsComponent implements OnInit {
  private readonly service = inject(GrowthAgentService);
  private readonly i18n = inject(LocalizationService);
  private readonly router = inject(Router);

  readonly items = signal<GrowthStrategyArtifact[]>([]);
  readonly dialogVisible = signal(false);
  readonly dialogMode = signal<'create' | 'edit'>('create');
  private editingId: string | null = null;
  private editingVersion = '1.0.0';
  private originalArtifactJson = '';

  model: StrategyArtifactFormModel = this.emptyModel();

  typeOptions() {
    return STRATEGY_ARTIFACT_TYPE_OPTIONS.map(value => ({
      value,
      label: this.t(`growth.strategy.artifactTypes.${value}`)
    }));
  }

  businessPlanOptions(): Array<{ label: string; value: string }> {
    return this.items()
      .filter(item => normalizeStrategyArtifactType(item.artifactType) === 'BusinessPlan')
      .map(item => ({ label: item.title, value: item.id }));
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.service.listStrategyArtifacts().subscribe(items => this.items.set(items));
  }

  artifactTypeLabel(artifactType: string | number): string {
    return this.t(strategyArtifactTypeI18nKey(artifactType));
  }

  strategyArtifactStatusLabel(status: string | number): string {
    const normalized = normalizeStrategyArtifactStatus(status);
    if (normalized === 'Draft') return this.t('growth.strategy.activation.status.draft');
    if (normalized === 'InReview') return this.t('growth.strategy.activation.status.pending');
    if (normalized === 'Approved') return this.t('growth.strategy.activation.status.approved');
    if (normalized === 'Archived') return this.t('growth.strategy.activation.status.archived');
    return String(status);
  }

  isStrategyArtifactDraft(status: string | number): boolean {
    return normalizeStrategyArtifactStatus(status) === 'Draft';
  }

  isStrategyArtifactInReview(status: string | number): boolean {
    return normalizeStrategyArtifactStatus(status) === 'InReview';
  }

  canEdit(row: GrowthStrategyArtifact): boolean {
    const status = normalizeStrategyArtifactStatus(row.status);
    return status === 'Draft' || status === 'InReview';
  }

  openCreateDialog(): void {
    this.dialogMode.set('create');
    this.editingId = null;
    this.editingVersion = '1.0.0';
    this.model = this.emptyModel();
    this.dialogVisible.set(true);
  }

  openEditDialog(row: GrowthStrategyArtifact): void {
    const artifact = this.parseArtifactFields(row.artifactJson);
    const swot = this.parseSwotFields(row.artifactJson);
    this.dialogMode.set('edit');
    this.editingId = row.id;
    this.editingVersion = row.version || '1.0.0';
    this.originalArtifactJson = row.artifactJson;
    this.model = {
      artifactType: normalizeStrategyArtifactType(row.artifactType),
      title: row.title,
      objective: artifact.objective,
      targetSegment: artifact.targetSegment,
      budgetRange: artifact.budgetRange,
      timeline: artifact.timeline,
      kpis: artifact.kpis,
      channels: artifact.channels,
      campaigns: artifact.campaigns,
      messaging: artifact.messaging,
      successMetrics: artifact.successMetrics,
      linkedBusinessPlanId: artifact.businessPlanId,
      strengths: swot.strengths,
      weaknesses: swot.weaknesses,
      opportunities: swot.opportunities,
      threats: swot.threats
    };
    this.dialogVisible.set(true);
  }

  canSave(): boolean {
    if (!this.model.title.trim()) {
      return false;
    }

    if (this.isBusinessPlanType()) {
      return Boolean(
        this.model.objective.trim()
        && this.model.targetSegment.trim()
        && this.model.budgetRange.trim()
        && this.model.timeline.trim()
        && this.model.kpis.trim()
      );
    }

    if (this.isMarketingPlanType()) {
      return Boolean(
        this.model.channels.trim()
        && this.model.campaigns.trim()
        && this.model.messaging.trim()
        && this.model.successMetrics.trim()
      );
    }

    return true;
  }

  save(): void {
    if (!this.canSave()) return;
    const artifactJson = this.dialogMode() === 'edit' && this.editingId && isRichStrategyArtifact(this.originalArtifactJson, this.model.artifactType)
      ? this.originalArtifactJson
      : this.buildArtifactJson();
    if (this.dialogMode() === 'edit' && this.editingId) {
      this.service.updateStrategyArtifact(this.editingId, {
        title: this.model.title.trim(),
        version: this.editingVersion,
        artifactJson,
        citationRefsJson: '[]'
      }).subscribe({
        next: () => {
          this.dialogVisible.set(false);
          this.load();
        }
      });
      return;
    }

    this.service.createStrategyArtifact({
      artifactType: this.model.artifactType,
      title: this.model.title.trim(),
      version: '1.0.0',
      artifactJson,
      citationRefsJson: '[]'
    }).subscribe({
      next: () => {
        this.dialogVisible.set(false);
        this.load();
      }
    });
  }

  submit(id: string): void {
    this.service.submitStrategyArtifact(id).subscribe(() => this.load());
  }

  approve(id: string): void {
    this.service.approveStrategyArtifact(id).subscribe(() => this.load());
  }

  canGenerateMarketingPlan(row: GrowthStrategyArtifact): boolean {
    return normalizeStrategyArtifactType(row.artifactType) === 'BusinessPlan'
      && normalizeStrategyArtifactStatus(row.status) === 'Approved';
  }

  canGenerateCalendar(row: GrowthStrategyArtifact): boolean {
    return normalizeStrategyArtifactType(row.artifactType) === 'MarketingPlan'
      && normalizeStrategyArtifactStatus(row.status) === 'Approved';
  }

  generateMarketingPlan(row: GrowthStrategyArtifact): void {
    const parsed = this.parseArtifactFields(row.artifactJson);
    const channels = parsed.channels || 'web-blog, web-landing, social-organic';
    const campaigns = parsed.campaigns || `${row.title} launch campaign`;
    const messaging = parsed.messaging || parsed.objective || row.title;
    const successMetrics = parsed.successMetrics || parsed.kpis;
    this.service.createStrategyArtifact({
      artifactType: 'MarketingPlan',
      title: `${row.title} · ${this.t('growth.strategy.artifactTypes.MarketingPlan')}`,
      version: '1.0.0',
      artifactJson: JSON.stringify({
        businessPlanId: row.id,
        objective: parsed.objective,
        targetSegment: parsed.targetSegment,
        budgetRange: parsed.budgetRange,
        timeline: parsed.timeline,
        kpis: parsed.kpis,
        channels,
        campaigns,
        messaging,
        successMetrics
      }),
      citationRefsJson: '[]'
    }).subscribe(() => this.load());
  }

  openActivationPlans(artifactId: string): void {
    this.router.navigate(['/growth/strategy/activation-plans/new'], { queryParams: { artifactId } });
  }

  linkedBusinessPlanTitle(row: GrowthStrategyArtifact): string {
    const artifact = this.parseArtifactFields(row.artifactJson);
    if (!artifact.businessPlanId) {
      return '—';
    }
    return this.items().find(item => item.id === artifact.businessPlanId)?.title ?? artifact.businessPlanId;
  }

  isBusinessPlanType(): boolean {
    return this.model.artifactType === 'BusinessPlan';
  }

  isMarketingPlanType(): boolean {
    return this.model.artifactType === 'MarketingPlan';
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  private emptyModel(): StrategyArtifactFormModel {
    return {
      artifactType: 'BusinessPlan',
      title: '',
      objective: '',
      targetSegment: '',
      budgetRange: '',
      timeline: '',
      kpis: '',
      channels: '',
      campaigns: '',
      messaging: '',
      successMetrics: '',
      linkedBusinessPlanId: null,
      strengths: '',
      weaknesses: '',
      opportunities: '',
      threats: ''
    };
  }

  private buildArtifactJson(): string {
    if (this.model.artifactType === 'BusinessPlan') {
      return JSON.stringify({
        objective: this.model.objective.trim(),
        targetSegment: this.model.targetSegment.trim(),
        budgetRange: this.model.budgetRange.trim(),
        timeline: this.model.timeline.trim(),
        kpis: this.model.kpis.trim()
      });
    }

    if (this.model.artifactType === 'MarketingPlan') {
      return JSON.stringify({
        businessPlanId: this.model.linkedBusinessPlanId,
        channels: this.model.channels.trim(),
        campaigns: this.model.campaigns.trim(),
        messaging: this.model.messaging.trim(),
        successMetrics: this.model.successMetrics.trim()
      });
    }

    return JSON.stringify({
      strengths: this.model.strengths.trim(),
      weaknesses: this.model.weaknesses.trim(),
      opportunities: this.model.opportunities.trim(),
      threats: this.model.threats.trim()
    });
  }

  private parseSwotFields(artifactJson: string): Pick<StrategyArtifactFormModel, 'strengths' | 'weaknesses' | 'opportunities' | 'threats'> {
    try {
      const parsed = JSON.parse(artifactJson) as Record<string, unknown>;
      return {
        strengths: typeof parsed['strengths'] === 'string' ? parsed['strengths'] : '',
        weaknesses: typeof parsed['weaknesses'] === 'string' ? parsed['weaknesses'] : '',
        opportunities: typeof parsed['opportunities'] === 'string' ? parsed['opportunities'] : '',
        threats: typeof parsed['threats'] === 'string' ? parsed['threats'] : ''
      };
    } catch {
      return { strengths: '', weaknesses: '', opportunities: '', threats: '' };
    }
  }

  private parseArtifactFields(artifactJson: string): {
    objective: string;
    targetSegment: string;
    budgetRange: string;
    timeline: string;
    kpis: string;
    channels: string;
    campaigns: string;
    messaging: string;
    successMetrics: string;
    businessPlanId: string | null;
  } {
    try {
      const parsed = JSON.parse(artifactJson) as Record<string, unknown>;
      return {
        objective: typeof parsed['objective'] === 'string' ? parsed['objective'] : '',
        targetSegment: typeof parsed['targetSegment'] === 'string' ? parsed['targetSegment'] : '',
        budgetRange: typeof parsed['budgetRange'] === 'string' ? parsed['budgetRange'] : '',
        timeline: typeof parsed['timeline'] === 'string' ? parsed['timeline'] : '',
        kpis: typeof parsed['kpis'] === 'string' ? parsed['kpis'] : '',
        channels: typeof parsed['channels'] === 'string' ? parsed['channels'] : '',
        campaigns: typeof parsed['campaigns'] === 'string' ? parsed['campaigns'] : '',
        messaging: typeof parsed['messaging'] === 'string' ? parsed['messaging'] : '',
        successMetrics: typeof parsed['successMetrics'] === 'string' ? parsed['successMetrics'] : '',
        businessPlanId: typeof parsed['businessPlanId'] === 'string' ? parsed['businessPlanId'] : null
      };
    } catch {
      return {
        objective: '',
        targetSegment: '',
        budgetRange: '',
        timeline: '',
        kpis: '',
        channels: '',
        campaigns: '',
        messaging: '',
        successMetrics: '',
        businessPlanId: null
      };
    }
  }
}
